/** @jsxImportSource @emotion/react */

import { css, List, ListItem, ListItemButton, Paper, useAutocomplete } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';
import { cloneDeep, debounce, find, isEmpty } from 'lodash';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Contact } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../auth/UserContext';
import Loading from '../components/Loading';
import { StyledTooltip } from '../components/StyledTooltip';
import { FETCH_POLICY_CONFIG } from '../constants/constants';
import { usePrevious } from '../hooks/usePrevious';
import { dropdownModifiers } from '../share/constants';
import { useStyling } from '../Theme';
import { MultiSelectInput } from './MultiSelectInput';
import { searchClient, slimSearchQuery } from './slimQuery';
import { useGridDropdownVisibility } from './useGridDropdownVisibility';
import { useMultiSelectField } from './useMultiSelectField';

interface LookupGridFieldProps extends GridRenderEditCellParams {
    multiSelect?: boolean;
    isReferenceType?: boolean;
    isRowSaving?: boolean;
    allowFreeText?: boolean;
    /** When true with allowFreeText=false, lets users clear optional lookup values in edit mode. */
    allowClear?: boolean;
    /** When true, clears uncommitted multi-select search text when the field loses focus. */
    clearUncommittedMultiSelectInputOnBlur?: boolean;
    reorderOptions?: boolean;
    filterOptions?: (options: Contact[], rowId: string | number) => any[];
    'data-testid'?: string;
}

export default function LookupGridField(props: LookupGridFieldProps) {
    const {
        id,
        value,
        field,
        OptionRenderer,
        toOption,
        entityToUpdate,
        columns,
        sortBy = 'name',
        isDesc = false,
        afterSearch = undefined,
        getNonFuzzyQueryParams = undefined,
        filters,
        filter,
        setIsRowUpdated = undefined,
        onAutoCreate = undefined,
        autoCreate = false,
        multiSelect = false,
        isReferenceType = false,
        isRowSaving = false,
        allowFreeText = true,
        allowClear = false,
        clearUncommittedMultiSelectInputOnBlur = false,
        reorderOptions = false,
        filterOptions = undefined,
        'data-testid': dataTestId
    } = props;
    const { theme } = useStyling();
    const apiRef = useGridApiContext();
    const [inputValue, setInputValue] = useState(multiSelect ? '' : (value ?? ''));
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const prevValue = usePrevious(value);
    const { activeOrganizationAccount } = useContext(UserContext);
    const [referenceElement, setReferenceElement] = useState<HTMLDivElement>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const isInitialMount = useRef(true);
    const getNonFuzzyQueryParamsRef = useRef(getNonFuzzyQueryParams);
    getNonFuzzyQueryParamsRef.current = getNonFuzzyQueryParams;
    const isNonFuzzyEmail = typeof getNonFuzzyQueryParams === 'function' && field === 'contactInfo.email.address';
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: dropdownModifiers
    });

    const updateFieldValue = useCallback(
        (values: object[]) => {
            const valueToSet = multiSelect ? values : values.length > 0 ? values[0] : undefined;
            apiRef.current.setEditCellValue({
                id: id,
                field: field,
                value: valueToSet
            });
        },
        [field, id, multiSelect, apiRef]
    );

    const multiSelectField = useMultiSelectField({
        initialValue: multiSelect ? value : undefined,
        isReferenceType,
        onUpdate: (values) => {
            if (typeof entityToUpdate === 'function') {
                const lastValue = values[values.length - 1];
                const updateArr = entityToUpdate(columns, field, { name: lastValue }, values);
                if (updateArr.length > 0) {
                    updateArr.forEach((update) => {
                        apiRef.current.setEditCellValue({
                            id: id,
                            field: update.field,
                            value: update.value
                        });
                    });
                    return;
                }
            }
            updateFieldValue(values as object[]);
        }
    });

    useEffect(() => {
        if (value !== prevValue) {
            if (multiSelect) {
                multiSelectField.updateFromExternal(value);
            } else {
                setInputValue(value ?? '');
            }
        }
    }, [value, prevValue, multiSelect, multiSelectField]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            if (value !== prevValue) {
                if (setIsRowUpdated) {
                    setIsRowUpdated(true);
                }
            }
        }
    }, [value, prevValue, setIsRowUpdated]);

    const getOptionText = useCallback((option) => String(option?.label ?? option?.name ?? option ?? ''), []);
    const orderedOptions = useMemo(() => {
        if (!reorderOptions) {
            return options;
        }

        const createOptions = options.filter((option) => option?.createOption);
        const nonCreateOptions = options.filter((option) => !option?.createOption);
        const normalizedInput = String(inputValue ?? '')
            .trim()
            .toLowerCase();
        if (!normalizedInput) {
            return [...nonCreateOptions, ...createOptions];
        }

        const startsWithMatches = nonCreateOptions.filter((option) =>
            getOptionText(option).trim().toLowerCase().startsWith(normalizedInput)
        );
        const startsWithMatchSet = new Set(startsWithMatches);
        const remainingOptions = nonCreateOptions.filter((option) => !startsWithMatchSet.has(option));

        return [...startsWithMatches, ...remainingOptions, ...createOptions];
    }, [reorderOptions, options, inputValue, getOptionText]);

    const { getRootProps, getInputProps, getListboxProps, getOptionProps, getTagProps, focused } = useAutocomplete({
        options: orderedOptions,
        getOptionLabel: getOptionText
    });
    const closeEditor = useCallback(() => {
        const input = referenceElement?.querySelector('input') as HTMLInputElement | null;
        input?.blur();
    }, [referenceElement]);

    const isReferenceVisible = useGridDropdownVisibility({
        referenceElement,
        focused,
        onClose: closeEditor
    });

    useEffect(() => {
        if (!focused || !popperElement || !update) return;
        // Force Popper to recalculate after first mount/content changes.
        const id = requestAnimationFrame(() => update());
        return () => cancelAnimationFrame(id);
    }, [focused, popperElement, loading, options.length, update]);

    const updateOptions = (term: string) => {
        const normalizedTerm = String(term ?? '').trim();
        const shouldSkipEmptyTermForNonFuzzy = isNonFuzzyEmail && normalizedTerm.length === 0;
        if (shouldSkipEmptyTermForNonFuzzy) {
            setOptions([]);
            if (afterSearch) {
                (async () => {
                    try {
                        const currentEditRows = apiRef.current.state.editRows;
                        const errorMessage = await afterSearch([], term, currentEditRows, id);
                        setErrorMessage(errorMessage || '');
                    } catch (error) {
                        console.error('Error occurred during afterSearch:', error);
                    }
                })();
            }
            return;
        }
        if (!loading) {
            if (!multiSelect && allowFreeText && normalizedTerm !== '') {
                apiRef.current.setEditCellValue({
                    id: id,
                    field: field,
                    value: term
                });
            }
            setLoading(true);
            setOptions([]);
            let queryTerm = term;
            const queryFilter = cloneDeep(filter);
            if (getNonFuzzyQueryParamsRef.current && typeof getNonFuzzyQueryParamsRef.current === 'function') {
                const query = getNonFuzzyQueryParamsRef.current(term);
                if (!isEmpty(query)) {
                    queryTerm = '';
                    queryFilter.expressions.push({ field: query.field, value: { values: [query.value] } });
                }
            }
            const entity = find(filters, { identifier: 'entity' })['value']; // TODO type this
            const variables = {
                entity: entity,
                query: queryTerm,
                page: { from: 0, size: 50 },
                tokenize: true,
                sortBy: undefined as string,
                filter: queryFilter
            };
            if (sortBy) {
                variables.sortBy = sortBy + '_' + (isDesc ? 'DESC' : 'ASC');
            }
            searchClient
                .query({
                    query: slimSearchQuery(entity),
                    variables: variables,
                    context: {
                        headers: {
                            ownerId: activeOrganizationAccount
                        }
                    },
                    fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
                })
                .then(async (result) => {
                    let options = result.data?.search.hits.items.map((item, index) => toOption(item, index));
                    const isExisting = options.some((option) => Object.values(option).includes(term));
                    if (term && autoCreate && allowFreeText && !isExisting) {
                        const createOption = {
                            label: term,
                            name: `Create ${term}`,
                            createOption: true
                        };
                        options.push(createOption);
                    }
                    if (filterOptions) {
                        options = filterOptions(options, id);
                    }
                    setOptions(options);
                    if (afterSearch) {
                        try {
                            const currentEditRows = apiRef.current.state.editRows;
                            const errorMessage = await afterSearch(
                                result.data?.search.hits.items,
                                term,
                                currentEditRows,
                                id
                            );
                            setErrorMessage(errorMessage || '');
                        } catch (error) {
                            console.error('afterSearch failed:', error);
                        }
                    }
                })
                .catch((err) => {
                    console.error('Update options failed:', err);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const updateOptionsRef = useRef(updateOptions);
    updateOptionsRef.current = updateOptions;

    const debouncedUpdateOptionsRef = useRef(
        debounce((term: string) => {
            updateOptionsRef.current(term);
        }, 500)
    );

    useEffect(() => {
        const debouncedFn = debouncedUpdateOptionsRef.current;
        return () => {
            debouncedFn.cancel();
        };
    }, []);

    useEffect(() => {
        if (
            (!isNonFuzzyEmail || !isEmpty(String(inputValue ?? '').trim())) &&
            focused &&
            !loading &&
            options.length === 0
        ) {
            debouncedUpdateOptionsRef.current('');
        }
    }, [focused, loading, options.length, inputValue, isNonFuzzyEmail]);

    useEffect(() => {
        if (isNonFuzzyEmail && isEmpty(String(inputValue ?? '').trim())) {
            setOptions([]);
            debouncedUpdateOptionsRef.current('');
            return;
        }
        if (multiSelect) {
            if (inputValue) {
                debouncedUpdateOptionsRef.current(inputValue);
            }
        } else {
            debouncedUpdateOptionsRef.current(inputValue ?? '');
        }
    }, [inputValue, multiSelect, isNonFuzzyEmail]);

    const setField = (option) => {
        const updateArr = entityToUpdate(columns, field, option);
        updateArr.forEach((update) => {
            apiRef.current.setEditCellValue({
                id: id,
                field: update.field,
                value: update.value
            });
        });
    };

    const handleAddValue = (option) => {
        if (multiSelect) {
            const valueToAdd = isReferenceType ? option : (option?.name ?? option?.label ?? option);
            multiSelectField.addValue(valueToAdd);
        } else {
            updateFieldValue([option?.label ?? option] as object[]);
        }
        setInputValue('');
    };

    const makeAutoCreateCall = async () => {
        if (typeof onAutoCreate !== 'function') return;
        const entity = find(filters, { identifier: 'entity' })?.value;
        try {
            const result = await onAutoCreate(inputValue, entity);
            const autoCreatedOption = toOption(result);
            const isExisting = options.some((opt) => Object.values(opt).includes(inputValue));
            const updatedOptions = isExisting ? options.filter((opt) => !opt.createOption) : [];
            const newOptions = [...updatedOptions, autoCreatedOption];
            newOptions.sort((a, b) => String(a.label ?? a).localeCompare(String(b.label ?? b)));
            setOptions(newOptions);
            if (multiSelect) {
                handleAddValue(autoCreatedOption);
            } else {
                setField(autoCreatedOption);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const updateField = (option) => async () => {
        if (option.createOption) {
            makeAutoCreateCall();
        } else {
            if (multiSelect) {
                handleAddValue(option);
            } else {
                setField(option);
            }
        }
        if (afterSearch) {
            try {
                const currentEditRows = apiRef.current.state.editRows;
                const errorMessage = await afterSearch(options, inputValue, currentEditRows, id);
                setErrorMessage(errorMessage || '');
            } catch (error) {
                console.error('afterSearch failed:', error);
            }
        }
    };

    useEffect(() => {
        if (loading) {
            setErrorMessage('');
        }
    }, [loading]);

    useEffect(() => {
        if (focused) {
            return;
        }

        if (clearUncommittedMultiSelectInputOnBlur && multiSelect) {
            if (inputValue) {
                setInputValue('');
            }
            return;
        }

        if (!allowFreeText && inputValue !== (value ?? '')) {
            if (allowClear && inputValue === '') {
                return;
            }

            setInputValue(value ?? '');
        }
    }, [focused, allowFreeText, allowClear, value, inputValue, multiSelect, clearUncommittedMultiSelectInputOnBlur]);

    return (
        <div
            style={{ width: '100%', height: '100%' }}
            ref={setReferenceElement}
            {...getRootProps()}
            data-testid={dataTestId}
        >
            <StyledTooltip open={!focused && !isEmpty(errorMessage)} title={errorMessage}>
                {multiSelect ? (
                    <MultiSelectInput
                        selectedValues={multiSelectField.selectedValues}
                        inputValue={inputValue}
                        onInputChange={setInputValue}
                        onRemove={multiSelectField.removeValue}
                        getInputProps={getInputProps}
                        getTagProps={getTagProps}
                        errorMessage={errorMessage}
                        focused={focused}
                        theme={theme}
                        readOnly={isRowSaving}
                    />
                ) : (
                    <input
                        {...getInputProps()}
                        css={css`
                            height: 100%;
                            width: 100%;
                            border: none;
                            padding-left: 8px;
                            padding-right: 8px;
                            font-size: inherit;
                            background-color: transparent;
                            color: ${theme.palette.text.primary};
                        `}
                        className={!focused && !isEmpty(errorMessage) ? 'Mui-error' : ''}
                        value={inputValue || ''}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            setInputValue(newValue);
                            if (!allowFreeText) {
                                if (allowClear && newValue === '') {
                                    apiRef.current.setEditCellValue({ id, field, value: '' });
                                }
                                return;
                            }
                            if (newValue !== (value ?? '')) {
                                apiRef.current.setEditCellValue({ id, field, value: newValue });
                            }
                        }}
                    />
                )}
            </StyledTooltip>
            {focused && isReferenceVisible ? (
                createPortal(
                    <div ref={setPopperElement} style={{ ...styles.popper, zIndex: 8000 }} {...attributes.popper}>
                        <Paper
                            sx={{
                                width: '200px',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                minHeight: '36px',
                                maxHeight: loading ? '100px' : '200px',
                                display: 'flex',
                                alignItems: loading ? 'center' : 'flex-start'
                            }}
                        >
                            {loading ? (
                                <Loading size="16px" />
                            ) : orderedOptions.length > 0 ? (
                                <List sx={{ height: '100%', width: '100%' }} {...getListboxProps()}>
                                    {orderedOptions.map((option, index) => (
                                        <ListItem
                                            sx={{ width: '100%' }}
                                            disablePadding
                                            {...getOptionProps({ option, index })}
                                            key={index}
                                            onClick={updateField(option)}
                                        >
                                            <ListItemButton sx={{ width: '100%' }}>
                                                <OptionRenderer option={option} />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Fragment />
                            )}
                        </Paper>
                    </div>,
                    document.querySelector('#popper')
                )
            ) : (
                <Fragment />
            )}
        </div>
    );
}
